import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useMaterialUIController, useGlobalMDAlert } from "context";
import { apiPost } from "lib/api";
import { FP } from "components/financepro";

// Helper unico para hablar con la API de DataOrbit.
export async function postDO(endpoint, body) {
  const { status, data } = await apiPost(endpoint, body);
  return { ok: status === 200, status, data };
}

// Credenciales de la sesion actual (mismo login que ya usa el usuario interno).
export function useCreds() {
  const [controller] = useMaterialUIController();
  const { usuarioLog, claveLog, nombreNegocio } = controller;
  return { usuario: usuarioLog, password: claveLog, nombrenegocio: nombreNegocio };
}

// Mapa de semaforo de riesgo compartido por las pantallas.
export const SEMAFORO = {
  verde: { label: "Bajo riesgo", color: FP.color.success, soft: FP.color.successSoft, variant: "success" },
  amarillo: { label: "Riesgo medio", color: FP.color.warning, soft: FP.color.warningSoft, variant: "warning" },
  rojo: { label: "Alto riesgo", color: FP.color.danger, soft: FP.color.dangerSoft, variant: "danger" },
};

export const fmtMoneyDO = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? "0").split(",").join("")) || 0;
  return `$${n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Panel inicial reutilizable (cuando aun no hay busqueda/lista): hero +
// pasos "como funciona" (configurables) + leyenda del semaforo. Sin KPIs.
export function IntroPanel({ title, subtitle, steps }) {
  return (
    <>
      <Box
        sx={{
          borderRadius: 2.5,
          p: { xs: 3, md: 4 },
          mb: 3,
          color: "#FFFFFF",
          position: "relative",
          overflow: "hidden",
          background: `linear-gradient(135deg, ${FP.color.navy} 0%, ${FP.color.navySoft} 100%)`,
          boxShadow: FP.shadow.navy,
        }}
      >
        <Box sx={{ position: "absolute", right: -40, top: -40, width: 220, height: 220, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.05)" }} />
        <Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ width: 52, height: 52, borderRadius: 2, bgcolor: "rgba(255,255,255,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <HubRoundedIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: "1.2rem", md: "1.5rem" }, lineHeight: 1.15 }}>
              {title}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.78)", fontSize: "0.9rem", mt: 0.4, maxWidth: 620 }}>
              {subtitle}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "3fr 2fr" } }}>
        <Card elevation={0} sx={{ borderRadius: 2.5, border: `1px solid ${FP.color.border}`, bgcolor: FP.color.cardBg, boxShadow: FP.shadow.soft, overflow: "hidden" }}>
          <Box sx={{ px: { xs: 2, md: 3 }, py: 1.6, borderBottom: `1px solid ${FP.color.border}`, bgcolor: "#FAFBFE" }}>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>Cómo funciona</Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            {steps.map((s, i) => {
              const StepIcon = s.icon;
              return (
                <Box key={s.t} sx={{ display: "flex", gap: 1.8, pb: i < steps.length - 1 ? 2.2 : 0 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <Box sx={{ width: 34, height: 34, borderRadius: "50%", bgcolor: FP.color.accentBlueSoft, color: FP.color.accentBlue, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "0.9rem", flexShrink: 0 }}>
                      {i + 1}
                    </Box>
                    {i < steps.length - 1 && <Box sx={{ flex: 1, width: 2, bgcolor: FP.color.border, my: 0.5 }} />}
                  </Box>
                  <Box sx={{ pt: 0.4 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                      {StepIcon && <StepIcon sx={{ fontSize: 18, color: FP.color.navy }} />}
                      <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.92rem" }}>{s.t}</Typography>
                    </Box>
                    <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.82rem", mt: 0.3 }}>{s.d}</Typography>
                  </Box>
                </Box>
              );
            })}
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ borderRadius: 2.5, border: `1px solid ${FP.color.border}`, bgcolor: FP.color.cardBg, boxShadow: FP.shadow.soft, overflow: "hidden" }}>
          <Box sx={{ px: { xs: 2, md: 3 }, py: 1.6, borderBottom: `1px solid ${FP.color.border}`, bgcolor: "#FAFBFE" }}>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>Semáforo de riesgo</Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            {[
              ["verde", "Sin atraso registrado en la red."],
              ["amarillo", "Atraso entre 1 y 90 días."],
              ["rojo", "Atraso mayor a 90 días o reportado incobrable."],
            ].map(([nivel, desc]) => {
              const s = SEMAFORO[nivel];
              return (
                <Box key={nivel} sx={{ display: "flex", alignItems: "flex-start", gap: 1.2, mb: 1.6 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: s.color, mt: 0.4, flexShrink: 0 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.86rem" }}>{s.label}</Typography>
                    <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.8rem" }}>{desc}</Typography>
                  </Box>
                </Box>
              );
            })}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2.5, pt: 2, borderTop: `1px solid ${FP.color.border}` }}>
              <LockRoundedIcon sx={{ fontSize: 16, color: FP.color.textMuted }} />
              <Typography sx={{ color: FP.color.textMuted, fontSize: "0.75rem" }}>
                Las demás empresas se muestran de forma anónima.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </>
  );
}

// Envuelve el contenido de cada pantalla: si la empresa no se ha unido
// (opt-in explicito), muestra la tarjeta de adhesion en vez del contenido.
export function JoinGate({ children }) {
  const creds = useCreds();
  const { showAlert } = useGlobalMDAlert();
  const [estado, setEstado] = useState(null); // null = cargando
  const [uniendo, setUniendo] = useState(false);
  const [contratoAbierto, setContratoAbierto] = useState(false);
  const [aceptado, setAceptado] = useState(false);

  const cargar = useCallback(() => {
    postDO("/api/dataorbit_estado", {
      usuario: creds.usuario,
      password: creds.password,
      nombrenegocio: creds.nombrenegocio,
    }).then(({ ok, data }) => setEstado(ok ? data : { opt_in: 0 }));
  }, [creds.usuario, creds.password, creds.nombrenegocio]);

  useEffect(() => {
    cargar();
  }, []);

  if (estado === null) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress sx={{ color: FP.color.navy }} />
      </Box>
    );
  }

  if (Number(estado.opt_in) === 1) return children;

  const abrirContrato = () => {
    setAceptado(false);
    setContratoAbierto(true);
  };

  const unirse = () => {
    setContratoAbierto(false);
    setUniendo(true);
    postDO("/api/dataorbit_unirse", {
      usuario: creds.usuario,
      password: creds.password,
      nombrenegocio: creds.nombrenegocio,
    })
      .then(({ ok, data }) => {
        if (ok) {
          showAlert({ message: "Te has unido a DataOrbit", color: "success", autoHideDuration: 3000 });
          cargar();
        } else {
          showAlert({ message: data.Error || "No se pudo unir", color: "error", autoHideDuration: 3000 });
        }
      })
      .finally(() => setUniendo(false));
  };

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2.5,
        border: `1px solid ${FP.color.border}`,
        bgcolor: FP.color.cardBg,
        boxShadow: FP.shadow.soft,
        overflow: "hidden",
      }}
    >
      <CardContent>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6} px={2}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 2,
              bgcolor: FP.color.accentBlueSoft,
              color: FP.color.accentBlue,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2.5,
            }}
          >
            <HubRoundedIcon sx={{ fontSize: 34 }} />
          </Box>
          <Typography sx={{ color: FP.color.textPrimary, fontWeight: 800, fontSize: "1.2rem", mb: 1 }}>
            Únete a DataOrbit
          </Typography>
          <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.92rem", textAlign: "center", maxWidth: 560, mb: 3 }}>
            DataOrbit es la red de información crediticia compartida. Al unirte, aportas el historial de
            riesgo de tus clientes y, a cambio, puedes consultar el comportamiento de pago de cualquier
            cédula en todas las empresas participantes. Solo consulta quien aporta.
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
              gap: 1.5,
              mb: 3.5,
              width: "100%",
              maxWidth: 620,
            }}
          >
            {[
              ["Buró compartido", "Consulta morosidad de otras empresas antes de prestar."],
              ["Tus datos anonimizados", "Las demás empresas no ven tu nombre en las consultas."],
              ["Tú decides", "Puedes salir de la red cuando quieras."],
            ].map(([t, d]) => (
              <Box key={t} sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, p: 1.6, textAlign: "left" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.6 }}>
                  <VerifiedRoundedIcon sx={{ fontSize: 16, color: FP.color.success }} />
                  <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.82rem" }}>{t}</Typography>
                </Box>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.76rem" }}>{d}</Typography>
              </Box>
            ))}
          </Box>
          <Button
            variant="contained"
            disabled={true}
            startIcon={<HubRoundedIcon />}
            onClick={abrirContrato}
            sx={{
              bgcolor: FP.color.navy,
              color: "#FFFFFF",
              borderRadius: 1.4,
              px: 3.5,
              height: 48,
              boxShadow: "none",
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.95rem",
              "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
              "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
            }}
          >
            {uniendo ? "Uniéndote..." : "Unirme a DataOrbit"}
          </Button>
        </Box>
      </CardContent>

      <Dialog
        open={contratoAbierto}
        onClose={() => setContratoAbierto(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            fontWeight: 800,
            color: FP.color.textPrimary,
            pr: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LockRoundedIcon sx={{ fontSize: 20, color: FP.color.navy }} />
            Términos y condiciones de DataOrbit
          </Box>
          <IconButton size="small" onClick={() => setContratoAbierto(false)} sx={{ color: FP.color.textSecondary }}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: FP.color.border }}>
          <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.82rem", lineHeight: 1.7, mb: 1 }}>
            El presente Contrato de Adhesión (en adelante, el &ldquo;Contrato&rdquo;) regula la incorporación y
            participación de la empresa usuaria (en adelante, la &ldquo;Empresa Participante&rdquo;) en la red de
            información crediticia compartida DataOrbit (en adelante, &ldquo;DataOrbit&rdquo; o la &ldquo;Red&rdquo;).
            La aceptación de los presentes términos constituye un acuerdo vinculante entre las partes.
          </Typography>

          <Box sx={{ "& > div": { mb: 1.4 } }}>
            <Box>
              <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.84rem" }}>
                PRIMERA. — Objeto.
              </Typography>
              <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.82rem", lineHeight: 1.7 }}>
                El objeto del presente Contrato es regular el intercambio recíproco de información sobre el
                comportamiento de pago y el nivel de riesgo crediticio de los clientes, entre las empresas
                adheridas a la Red, con la exclusiva finalidad de evaluar la capacidad y el historial de pago
                de los titulares consultados.
              </Typography>
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.84rem" }}>
                SEGUNDA. — Aporte de información.
              </Typography>
              <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.82rem", lineHeight: 1.7 }}>
                La Empresa Participante autoriza de forma expresa e irrevocable, durante la vigencia del presente
                Contrato, la transmisión a la Red del historial de pago y de riesgo de sus clientes, declarando
                contar con la base de legitimación y los consentimientos necesarios de los titulares de los datos
                conforme a la normativa de protección de datos personales aplicable.
              </Typography>
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.84rem" }}>
                TERCERA. — Reciprocidad.
              </Typography>
              <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.82rem", lineHeight: 1.7 }}>
                El acceso a las funciones de consulta de la Red se encuentra condicionado al aporte efectivo y
                actualizado de información por parte de la Empresa Participante. El incumplimiento de dicha
                obligación facultará a DataOrbit a suspender o restringir el acceso a las consultas.
              </Typography>
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.84rem" }}>
                CUARTA. — Confidencialidad y anonimato.
              </Typography>
              <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.82rem", lineHeight: 1.7 }}>
                La identidad de la Empresa Participante permanecerá anónima frente a las demás empresas de la Red
                respecto de las consultas que realice. La Empresa Participante se obliga a mantener la más estricta
                confidencialidad sobre la información obtenida.
              </Typography>
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.84rem" }}>
                QUINTA. — Uso de la información.
              </Typography>
              <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.82rem", lineHeight: 1.7 }}>
                La información obtenida a través de la Red será utilizada única y exclusivamente para la evaluación
                del riesgo crediticio. Queda expresamente prohibida su cesión, comercialización o divulgación a
                terceros ajenos a la Red, así como cualquier uso distinto del autorizado en el presente Contrato.
              </Typography>
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.84rem" }}>
                SEXTA. — Vigencia y terminación.
              </Typography>
              <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.82rem", lineHeight: 1.7 }}>
                El presente Contrato tendrá vigencia indefinida. La Empresa Participante podrá darlo por terminado
                en cualquier momento, lo que conllevará el cese inmediato de su facultad de aportar y consultar
                información en la Red, sin perjuicio de las obligaciones de confidencialidad que subsistan.
              </Typography>
            </Box>
          </Box>

          <FormControlLabel
            sx={{ mt: 1.5, alignItems: "flex-start" }}
            control={
              <Checkbox
                checked={aceptado}
                onChange={(e) => setAceptado(e.target.checked)}
                sx={{ pt: 0, color: FP.color.navy, "&.Mui-checked": { color: FP.color.navy } }}
              />
            }
            label={
              <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.84rem", fontWeight: 600 }}>
                Declaro haber leído, comprendido y aceptado en su totalidad los términos y condiciones del presente
                Contrato de Adhesión a DataOrbit, obligándome a su cumplimiento.
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setContratoAbierto(false)}
            sx={{ textTransform: "none", fontWeight: 700, color: FP.color.textSecondary }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={!aceptado || uniendo}
            startIcon={<HubRoundedIcon />}
            onClick={unirse}
            sx={{
              bgcolor: FP.color.navy,
              color: "#FFFFFF",
              borderRadius: 1.4,
              px: 3,
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
              "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
              "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
            }}
          >
            Aceptar y unirme
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
